/**
 * Format minutes to a human-readable duration string
 * Shows minutes if less than 1 hour, otherwise rounds to hours
 * Examples:
 *   - 5 minutes -> "5m"
 *   - 30 minutes -> "30m"
 *   - 90 minutes -> "2h"
 *   - 210 minutes (3.5 hours) -> "4h"
 *   - 1500 minutes (25 hours) -> "1d 1h"
 *   - 3600 minutes (2.5 days) -> "2d 12h"
 *   - 8562 minutes (142.7 hours) -> "6d"
 */
export function formatDuration(minutes: number): string {
  // If less than 1 hour, show minutes
  if (minutes < 60) {
    return `${Math.round(minutes)}m`;
  }

  // Round to nearest hour for 1+ hours
  const totalHours = Math.round(minutes / 60);

  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;

  const parts: string[] = [];

  if (days > 0) {
    parts.push(`${days}d`);
  }

  if (hours > 0) {
    parts.push(`${hours}h`);
  }

  // If everything is 0, return "0h" (shouldn't happen with the above logic)
  if (parts.length === 0) {
    return "0h";
  }

  return parts.join(" ");
}

/**
 * Format a timestamp to a readable relative time
 * Examples:
 *   - "5m ago"
 *   - "2h 30m ago"
 *   - "1d 3h ago"
 *
 * @param timestamp - ISO timestamp string or Date object
 * @returns Formatted relative time string
 */
export function formatRelativeTime(timestamp: string | Date): string {
  const date = typeof timestamp === "string" ? new Date(timestamp) : timestamp;
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  return `${formatDuration(diffMinutes)} ago`;
}

/**
 * Format a Date object to time string (e.g., "2:30 PM")
 *
 * @param date - Date object to format
 * @returns Formatted time string
 */
export function formatTime(date: Date): string {
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Format a Date object to full date and time string
 * Example: "Oct 30, 6:11 PM PDT"
 *
 * @param date - Date object to format
 * @returns Formatted date and time string
 */
export function formatFullDateTime(date: Date): string {
  const monthDay = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  const time = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
  return `${monthDay}, ${time}`;
}

/**
 * Format an ISO timestamp string to full date and time string
 * Handles both ISO 8601 timestamps and PostgreSQL timestamps
 * Accounts for timezone issues where DB timestamps may not include timezone info
 *
 * @param timestamp - ISO timestamp string or PostgreSQL timestamp
 * @returns Formatted date and time string
 */
export function formatTimestamp(timestamp: string): string {
  try {
    // Handle PostgreSQL timestamps that might have space instead of T
    let isoTimestamp = timestamp.replace(" ", "T");

    // If timestamp doesn't end with Z and doesn't have a timezone offset, treat it as UTC
    if (
      !isoTimestamp.endsWith("Z") &&
      !isoTimestamp.match(/[+-]\d{2}:\d{2}$/)
    ) {
      isoTimestamp += "Z";
    }

    const date = new Date(isoTimestamp);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn(`Invalid timestamp: ${timestamp}`);
      return timestamp; // Return original string if invalid
    }

    return formatFullDateTime(date);
  } catch (error) {
    console.error(`Error formatting timestamp ${timestamp}:`, error);
    return timestamp; // Return original string on error
  }
}
